import { useDraggable } from '@dnd-kit/core';
import { usePlaytesterStore } from '../../stores/playtester-store.js';
import type { CardInstance } from '@mtg-deck-tool/shared';
import { getCardImageUri } from '@mtg-deck-tool/shared';
import { CARD_CORNER_RADIUS } from '../../lib/playtester-engine.js';

function RevealCard({
  instance,
  isLast,
}: {
  instance: CardInstance;
  isLast: boolean;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `reveal-${instance.instanceId}`,
    data: { instanceId: instance.instanceId, fromRevealPanel: true },
  });

  // Use 'large' for crisp rendering; shows full card frame with P/T box
  const imageUri = getCardImageUri(instance.card, 'large');
  const isToken = instance.card.layout === 'token';

  return (
    <div className="flex flex-col items-center gap-1 flex-shrink-0">
      {/* Card art */}
      <div
        ref={setNodeRef}
        {...listeners}
        {...attributes}
        className={[
          'relative rounded overflow-hidden cursor-grab',
          isDragging ? 'opacity-30' : '',
          isLast && !isToken ? 'ring-2 ring-amber-400' : 'ring-1 ring-gray-600',
        ].join(' ')}
        style={{ width: 'var(--pt-card-w)', height: 'var(--pt-card-h)' }}
        title={instance.card.name}
      >
        {imageUri ? (
          <img src={imageUri} alt={instance.card.name} className="w-full h-full object-cover" style={{ borderRadius: CARD_CORNER_RADIUS }} />
        ) : (
          <div className="w-full h-full bg-gray-700 flex items-center justify-center">
            <span className="text-gray-400 text-[8px] text-center px-1">{instance.card.name}</span>
          </div>
        )}

        {/* Non-token badge */}
        {isLast && !isToken && (
          <div className="absolute bottom-0 inset-x-0 bg-amber-600/90 text-white text-[9px] text-center py-0.5 pointer-events-none">
            Non-Token
          </div>
        )}
      </div>

      {/* Name + type below */}
      <div className="text-center" style={{ width: 'var(--pt-card-w)' }}>
        <p className="text-white text-[9px] leading-tight truncate">{instance.card.name}</p>
        <p className="text-gray-500 text-[8px] leading-tight truncate">{instance.card.typeLine}</p>
      </div>
    </div>
  );
}

export function HordeRevealPanel() {
  const revealPanel = usePlaytesterStore((s) => s.horde?.revealPanel ?? []);

  // "Begin My Turn" [N] lives in PlaytesterButtonColumn — not duplicated here.
  // HordePhaseIndicator above already shows the phase label — not duplicated here.

  if (revealPanel.length === 0) {
    return (
      <div className="px-3 py-2 rounded border border-gray-700 bg-gray-800/30">
        <span className="text-gray-400 text-sm">Horde library exhausted — press N to begin your turn.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 border border-amber-800/60 rounded bg-amber-950/10 p-3">
      {/* Horizontal scrollable strip — cards at full zoom-aware size */}
      <div className="flex gap-3 overflow-x-auto pb-1">
        {revealPanel.map((instance, idx) => (
          <RevealCard
            key={instance.instanceId}
            instance={instance}
            isLast={idx === revealPanel.length - 1}
          />
        ))}
      </div>
    </div>
  );
}
